<div [class.rtl]="lang == 'ar'" class="add-parent-modal" [class.add-parent-modal--wide]="step === 2">

    <!-- where the admin is, and what is still ahead -->
    <ol class="ap-steps" [attr.aria-label]="'add-parent-flow.title' | translate">
        <li class="ap-step" [class.ap-step--active]="step === 1" [class.ap-step--done]="step > 1">
            <span class="ap-step__dot">
                <svg *ngIf="step > 1" width="11" height="11" viewBox="0 0 16 16" fill="none">
                    <path d="M13.3 4.6 6.4 11.5 2.9 8" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
                <ng-container *ngIf="step === 1">{{ 1 | arabicNumber: lang }}</ng-container>
            </span>
            <span class="ap-step__label">{{'add-parent-flow.step-student' | translate}}</span>
        </li>
        <li class="ap-step__line" aria-hidden="true"></li>
        <li class="ap-step" [class.ap-step--active]="step === 2">
            <span class="ap-step__dot">{{ 2 | arabicNumber: lang }}</span>
            <span class="ap-step__label">{{'add-parent-flow.step-details' | translate}}</span>
        </li>
    </ol>

    <!-- ===================== step 1: which student ===================== -->
    <div *ngIf="step === 1" class="ap-body ap-body--pick">
        <h2 class="ap-question">{{'add-parent-flow.question' | translate}}</h2>
        <p class="ap-hint">{{'add-parent-flow.hint' | translate}}</p>

        <label class="ap-label" for="addParentStudentSearch">
            {{'add-parent-flow.student-label' | translate}}<span class="ap-required">*</span>
        </label>

        <!-- nothing picked yet: the search box and what it found -->
        <div *ngIf="!selectedStudent" #studentCombo class="ap-combo">
            <div class="ap-search" [class.ap-search--open]="resultsOpen && canSearch">
                <svg class="ap-search__icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="7.1" cy="7.1" r="5.1" stroke="currentColor" stroke-width="1.6" />
                    <path d="m11 11 3 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                </svg>
                <input id="addParentStudentSearch" class="ap-search__input" type="text" autocomplete="off"
                    [value]="searchTerm" (input)="onSearchInput($event.target.value)"
                    (focus)="resultsOpen = true"
                    [placeholder]="'add-parent-flow.search-placeholder' | translate">
                <button *ngIf="searchTerm" type="button" class="ap-search__clear" (click)="onSearchInput('')"
                    [attr.aria-label]="'add-parent-flow.clear' | translate">&times;</button>
            </div>

            <div *ngIf="resultsOpen && canSearch" class="ap-results" role="listbox">
                <div *ngIf="searching" class="ap-results__state">
                    <mat-spinner diameter="18"></mat-spinner>
                    <span>{{'add-parent-flow.searching' | translate}}</span>
                </div>

                <ng-container *ngIf="!searching && !searchFailed">
                    <button *ngFor="let student of results; trackBy: trackById" type="button" class="ap-result"
                        role="option" (click)="selectStudent(student)">
                        <span class="ap-result__name">{{ student.name }}</span>
                        <span class="ap-result__meta">
                            <ng-container *ngIf="studentMeta(student)">{{ studentMeta(student) }}</ng-container>
                            <ng-container *ngIf="studentMeta(student) && student.national_id"> • </ng-container>
                            <ng-container *ngIf="student.national_id">
                                {{'add-parent-flow.id-label' | translate}}
                                <span data-pii="redact">{{ student.national_id }}</span>
                            </ng-container>
                        </span>
                    </button>
                </ng-container>

                <!-- nothing matched: say so, and say what to try -->
                <div *ngIf="showsEmptyState" class="ap-empty">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                        <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" stroke-width="1.5" />
                        <path d="m15.5 15.5 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                        <path d="m8.4 8.4 4.2 4.2m0-4.2-4.2 4.2" stroke="currentColor" stroke-width="1.5"
                            stroke-linecap="round" />
                    </svg>
                    <p class="ap-empty__title">{{'add-parent-flow.no-results-title' | translate}}</p>
                    <p class="ap-empty__hint">{{'add-parent-flow.no-results-hint' | translate}}</p>
                </div>

                <!-- the list itself could not be loaded: not the same as no matches -->
                <div *ngIf="searchFailed && !searching" class="ap-empty ap-empty--error">
                    <p class="ap-empty__title">{{'add-parent-flow.search-failed' | translate}}</p>
                    <button type="button" class="ap-retry" (click)="retrySearch()">
                        {{'add-parent-flow.retry' | translate}}
                    </button>
                </div>
            </div>

            <p *ngIf="studentServerError" class="ap-note ap-note--error">{{ studentServerError }}</p>
        </div>

        <!-- picked: the card stands in for the field, and can be swapped -->
        <div *ngIf="selectedStudent" class="ap-picked">
            <div class="ap-picked__body">
                <span class="ap-picked__name">{{ selectedStudent.name }}</span>
                <!-- one labelled row each: a single run of name, grade, class and
                     ID separated by bullets reorders itself in Arabic -->
                <dl class="ap-facts">
                    <div class="ap-fact" *ngIf="selectedStudent.national_id">
                        <dt class="ap-fact__label">{{'user-management.students.national-id' | translate}}</dt>
                        <dd class="ap-fact__value" data-pii="redact">{{ selectedStudent.national_id }}</dd>
                    </div>
                    <div class="ap-fact" *ngIf="selectedStudent.grade">
                        <dt class="ap-fact__label">{{'user-management.students.grade' | translate}}</dt>
                        <dd class="ap-fact__value">{{ selectedStudent.grade }}</dd>
                    </div>
                    <div class="ap-fact" *ngIf="selectedStudent.class">
                        <dt class="ap-fact__label">{{'user-management.students.class' | translate}}</dt>
                        <dd class="ap-fact__value">{{ selectedStudent.class }}</dd>
                    </div>
                </dl>
            </div>
            <button type="button" class="ap-picked__change" (click)="clearSelectedStudent()">
                {{'add-parent-flow.change-student' | translate}}
            </button>
        </div>
    </div>

    <!-- ===================== step 2: who the parent is ===================== -->
    <div *ngIf="step === 2" class="ap-body ap-body--form">
        <!-- the context the whole flow hangs on, kept in view -->
        <div class="ap-context">
            <span class="ap-context__label">{{'add-parent-flow.adding-for' | translate}}</span>
            <span class="ap-context__name">{{ selectedStudent?.name }}</span>
            <span *ngIf="studentMeta(selectedStudent)" class="ap-context__meta">{{ studentMeta(selectedStudent) }}</span>
            <button type="button" class="ap-context__change" (click)="backToStudentStep()">
                {{'add-parent-flow.change-student' | translate}}
            </button>
        </div>

        <form [formGroup]="addParentForm">
            <div class="img-section">
                <div class="avatar-picker" (click)="changeParentProfile()">
                    <img *ngIf="image" class="avatar-picker__image" [src]="image">
                    <svg *ngIf="!image" class="avatar-picker__placeholder" xmlns="http://www.w3.org/2000/svg"
                        width="39.5" height="45.408" viewBox="0 0 39.5 45.408">
                        <path d="M41.4,36.893V30H19v6.893a11.2,11.2,0,0,0,22.4,0Z" transform="translate(-10.452 -9.731)"
                            fill="#707070" />
                        <path d="M26,51.74v3.783l5.17.741,5.17-.741V51.74A12.853,12.853,0,0,1,26,51.74Z"
                            transform="translate(-11.42 -13.449)" fill="#707070" />
                        <path d="M47,35.17A2.585,2.585,0,1,0,47,30Z" transform="translate(-14.325 -9.115)"
                            fill="#707070" />
                        <path d="M10.6,12.553l18.44,6.911,18.44-6.911L29.04,4.1Z" transform="translate(-9.29 -4.1)"
                            fill="#707070" />
                        <path
                            d="M43.478,55H35.723v1.723a.862.862,0,0,1-.741.853l-6.032.862a.849.849,0,0,1-.241,0l-6.032-.862a.862.862,0,0,1-.741-.853V55H14.181a5.185,5.185,0,0,0-5.1,4.308h39.5A5.185,5.185,0,0,0,43.478,55Z"
                            transform="translate(-9.08 -13.9)" fill="#707070" />
                        <path
                            d="M30.064,23.284a.888.888,0,0,1-.3-.052L18,18.82v7.91H42.127V18.82L30.365,23.232a.888.888,0,0,1-.3.052Z"
                            transform="translate(-10.314 -7.006)" fill="#707070" />
                        <path d="M16.585,35.17V30a2.585,2.585,0,0,0,0,5.17Z" transform="translate(-9.761 -9.115)"
                            fill="#707070" />
                    </svg>
                    <svg class="avatar-picker__camera" xmlns="http://www.w3.org/2000/svg" width="25.673" height="25.673"
                        viewBox="0 0 25.673 25.673">
                        <path
                            d="M12.836,0A12.836,12.836,0,1,0,25.673,12.836,12.851,12.851,0,0,0,12.836,0Zm0,0"
                            fill="#1d71d3" />
                        <g transform="translate(5.41 6.492)">
                            <path
                                d="M15.19,46.727a1.689,1.689,0,0,0-1.24-.523H11.471v-.03a1.341,1.341,0,0,0-.4-.971,1.367,1.367,0,0,0-.971-.4H5.616a1.377,1.377,0,0,0-1.389,1.374v.03H1.762a1.689,1.689,0,0,0-1.24.523A1.776,1.776,0,0,0,0,47.966V55.4a1.689,1.689,0,0,0,.523,1.24,1.776,1.776,0,0,0,1.24.523H13.95a1.689,1.689,0,0,0,1.24-.523,1.776,1.776,0,0,0,.523-1.24V47.966A1.689,1.689,0,0,0,15.19,46.727ZM14.936,55.4h-.015a.969.969,0,0,1-.971.971H1.762A.969.969,0,0,1,.792,55.4V47.966A.969.969,0,0,1,1.762,47H4.645a.4.4,0,0,0,.4-.4v-.433a.562.562,0,0,1,.582-.582H10.1a.562.562,0,0,1,.582.582v.433a.4.4,0,0,0,.4.4h2.883a.969.969,0,0,1,.971.971Z"
                                transform="translate(0 -44.8)" fill="#fff" />
                            <path
                                d="M115.674,130.8a3.672,3.672,0,1,0,2.6,1.075A3.681,3.681,0,0,0,115.674,130.8Zm2.031,5.72a2.885,2.885,0,0,1-4.062,0,2.855,2.855,0,0,1-.836-2.031,2.919,2.919,0,0,1,.836-2.031,2.855,2.855,0,0,1,2.031-.836,2.919,2.919,0,0,1,2.031.836,2.855,2.855,0,0,1,.836,2.031A2.8,2.8,0,0,1,117.705,136.52Z"
                                transform="translate(-107.818 -127.589)" fill="#fff" />
                            <circle cx="0.732" cy="0.732" r="0.732" transform="translate(12.441 3.196)" fill="#fff" />
                        </g>
                    </svg>
                </div>
                <input #fileUploadInput (change)="onFileSelected($event.target.files)" type="file"
                    style="display: none;">
            </div>

            <div class="secondary-info">
                <div class="row">
                    <!-- left: the same fields, rules and order the Edit Parent modal uses -->
                    <div class="secondary-info-text-section col-lg-6">
                        <mat-form-field appearance="outline" dir="ltr">
                            <mat-label>{{'edit-parent.parent-name' | translate}}</mat-label>
                            <input matInput formControlName="name">
                            <mat-error>{{getValidationError('name')}}</mat-error>
                        </mat-form-field>

                        <mat-form-field appearance="outline" class="national-id" dir="ltr"
                            [appDisableByPermission]="'ParentEditNationalId'">
                            <mat-label>{{'edit-parent.id-number' | translate}}</mat-label>
                            <input matInput formControlName="national_id" data-pii="redact"
                                [appDisableByPermission]="'ParentEditNationalId'"
                                (input)="onIdentityEdited('parent_national_id')">
                            <mat-error>{{getValidationError('national_id')}}</mat-error>
                        </mat-form-field>
                        <!-- parent_national_id, and the terminal cases that name a guardian.
                             Sibling of the field, never a child of it. -->
                        <app-conflict-resolver *ngIf="showsConflictAt('parent_national_id')" [conflict]="conflict"
                            [resolutions]="resolutions" [lang]="lang" (choose)="onConflictChoice($event)"
                            (undo)="onConflictUndo($event)"></app-conflict-resolver>

                        <mat-form-field appearance="outline" dir="ltr">
                            <mat-label>{{'edit-parent.gender' | translate}}</mat-label>
                            <mat-select formControlName="gender" panelClass="parentGenderClass">
                                <mat-option value="female">{{'edit-parent.female' | translate}}</mat-option>
                                <mat-option value="male">{{'edit-parent.male' | translate}}</mat-option>
                            </mat-select>
                            <mat-error>{{'edit-parent.gender-required' | translate}}</mat-error>
                        </mat-form-field>

                        <div class="phone-no">
                            <mat-form-field class="code" appearance="outline" dir="ltr">
                                <mat-label>{{'edit-parent.code' | translate}}</mat-label>
                                <mat-select formControlName="country_code" [appDisableByPermission]="'ParentEditPhone'"
                                    (selectionChange)="onIdentityEdited('parent_phone')">
                                    <ng-container *ngFor="let country of countries">
                                        <mat-option *ngIf="country.selected" [value]="country?.code_plus">
                                            <table [ngStyle]="{'direction': lang == 'ar' ? 'rtl' : 'ltr'}">
                                                <tr>
                                                    <td>
                                                        <img style="margin-inline-end:5px;" [src]="country?.flag"
                                                            [alt]="country?.short"
                                                            [title]="lang == 'ar'? country.name_ar : country.name">
                                                    </td>
                                                    <td>{{country?.code_plus}}</td>
                                                </tr>
                                            </table>
                                        </mat-option>
                                    </ng-container>
                                </mat-select>
                                <mat-error>{{'edit-parent.code-required' | translate}}</mat-error>
                            </mat-form-field>
                            <mat-form-field appearance="outline" dir="ltr" [appDisableByPermission]="'ParentEditPhone'">
                                <mat-label>{{'edit-parent.phone' | translate}}</mat-label>
                                <input matInput formControlName="phone" (input)="onIdentityEdited('parent_phone')">
                                <mat-error *ngIf="phoneServerErrors?.length">{{ phoneServerErrors[0] }}</mat-error>
                                <mat-error *ngIf="!phoneServerErrors?.length">{{getValidationError('phone')}}</mat-error>
                            </mat-form-field>
                        </div>
                        <!-- sibling of .phone-no, not a child: the code/phone row is a
                             grid and would otherwise capture the panel -->
                        <app-conflict-resolver *ngIf="showsConflictAt('parent_phone')" [conflict]="conflict"
                            [resolutions]="resolutions" [lang]="lang" (choose)="onConflictChoice($event)"
                            (undo)="onConflictUndo($event)"></app-conflict-resolver>

                        <mat-form-field appearance="outline" dir="ltr">
                            <mat-label>{{'edit-parent.email' | translate}}</mat-label>
                            <input matInput formControlName="email" (input)="onIdentityEdited('parent_email')">
                            <mat-error>{{getValidationError('email')}}</mat-error>
                        </mat-form-field>
                        <app-conflict-resolver *ngIf="showsConflictAt('parent_email')" [conflict]="conflict"
                            [resolutions]="resolutions" [lang]="lang" (choose)="onConflictChoice($event)"
                            (undo)="onConflictUndo($event)"></app-conflict-resolver>
                    </div>

                    <!-- right: the map, where Edit Parent lists the students -->
                    <div class="secondary-info-map-section col-lg-6">
                        <app-map [mapView]="mapView" [mapControls]="mapControls" [showSearchBar]="true"
                            [showCurrentLocationBtn]="true" [showMarker]="true" [isMarkerDraggable]="true"
                            [showAddressSection]="false" [showCircles]="false"
                            (eventHandle)="mapEventHandle($event)"></app-map>
                        <div class="ap-address">
                            <span class="ap-address__label">{{'edit-parent.address' | translate}}</span>
                            <div class="ap-address__row">
                                <p class="ap-address__value" [title]="address">{{ address || '-' }}</p>
                                <button *ngIf="address" type="button" class="ap-address__clear" (click)="clearAddress()"
                                    [attr.aria-label]="'edit-parent.address' | translate">&times;</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
    <!-- Back / Continue / Save are drawn by the modal shell, in the header. -->
</div>
